import {
  chainDynamicValue,
  DynamicValue,
  useComponentState,
} from "@faedev/dynamic-values";
import { composeClassNames } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import {
  Accordion,
  AccordionCollapse,
  AccordionToggle,
  Card,
  Col,
  Form,
} from "react-bootstrap";
import FormSelect from "src/components/FormSelect";
import Heading from "src/components/Heading";
import Text from "src/components/Text";
import Translate from "src/components/Translate";
import onChangeToValue from "src/functions/onChangeToValue";
import optionsDifficulty from "src/functions/optionsDifficulty";
import optionsFocus from "src/functions/optionsFocus";
import optionsLocale from "src/functions/optionsLocale";
import optionsStyle from "src/functions/optionsStyle";
import translate from "src/functions/translate";
import { sFormAccordion } from "src/styles/sFormAccordion.module.scss";
import { sFormControl } from "src/styles/sFormControl.module.scss";
import { sFormLabel } from "src/styles/sFormLabel.module.scss";
import { sNote } from "src/styles/sNote.module.scss";
import { DataJoinUsForm } from "src/types/DataJoinUsForm";

type Props = { formData: DynamicValue<DataJoinUsForm> };

function FormJoinUsYogaProfile({ formData }: Props) {
  var toggleAccordion = useComponentState(false);
  const { locale: lang } = useRouter();
  return (
    <Accordion
      defaultActiveKey={`1`}
      className={composeClassNames([sFormAccordion, "joinus-yoga-profile"])}
    >
      <Card>
        <AccordionToggle eventKey={`0`}>
          <Heading
            type={`h2`}
            title={
              <>
                {`2. `}
                <Translate tKey={`Yoga profile`} />
                <Text size={`md`} width={`regular`}>
                  {`8 `}
                  <Translate tKey={`min.`} />
                </Text>
              </>
            }
          />
        </AccordionToggle>
        <AccordionCollapse eventKey={`0`}>
          <Card.Body>
            <Form.Row>
              <Col sm={8} xs={12}>
                <Form.Group>
                  <Form.Label className={sFormLabel}>
                    <Translate tKey={`Yoga Name`} />
                  </Form.Label>
                  <Form.Control
                    className={sFormControl}
                    name={`yoga-name`}
                    id={`yoga-name`}
                    onChange={function (param2) {
                      return chainDynamicValue(formData, "yoga-name").change(
                        onChangeToValue(param2)
                      );
                    }}
                    value={chainDynamicValue(formData, "yoga-name").value}
                  />
                  <Form.Text className={sNote}>
                    <Translate
                      tKey={`Only mention if you have been given a yoga name or people know you by a nickname.`}
                    />
                  </Form.Text>
                </Form.Group>
              </Col>
            </Form.Row>
            <Form.Group>
              <Form.Label className={sFormLabel}>
                <Translate tKey={`Preferred yoga styles`} />
              </Form.Label>
              <Form.Row>
                <Col sm={4} xs={12}>
                  <Form.Control
                    type={`select`}
                    as={`select`}
                    className={sFormControl}
                    id={`yoga-style-1`}
                    name={`yoga-style-1`}
                    onChange={function (param2) {
                      return chainDynamicValue(formData, "yoga-style-1").change(
                        onChangeToValue(param2)
                      );
                    }}
                    value={chainDynamicValue(formData, "yoga-style-1").value}
                  >
                    {optionsStyle(lang).map((item) => {
                      return (
                        <option key={item.value} value={item.value}>
                          {item.title}
                        </option>
                      );
                    })}
                  </Form.Control>
                </Col>
                <Col sm={4} xs={12}>
                  <Form.Control
                    type="select"
                    as={`select`}
                    className={sFormControl}
                    id={`yoga-style-2`}
                    name={`yoga-style-2`}
                    onChange={function (param2) {
                      return chainDynamicValue(formData, "yoga-style-2").change(
                        onChangeToValue(param2)
                      );
                    }}
                    value={chainDynamicValue(formData, "yoga-style-2").value}
                  >
                    {optionsStyle(lang).map((item) => {
                      return (
                        <option key={item.value} value={item.value}>
                          {item.title}
                        </option>
                      );
                    })}
                  </Form.Control>
                </Col>
                <Col sm={4} xs={12}>
                  <Form.Control
                    type="select"
                    as={`select`}
                    className={sFormControl}
                    id={`yoga-style-3`}
                    name={`yoga-style-3`}
                    onChange={function (param2) {
                      return chainDynamicValue(formData, "yoga-style-3").change(
                        onChangeToValue(param2)
                      );
                    }}
                    value={chainDynamicValue(formData, "yoga-style-3").value}
                  >
                    {optionsStyle(lang).map((item) => {
                      return (
                        <option key={item.value} value={item.value}>
                          {item.title}
                        </option>
                      );
                    })}
                  </Form.Control>
                </Col>
              </Form.Row>
              <Form.Text className={sNote}>
                <Translate
                  tKey={`Enter the min. 1 and max. 3 yoga styles that you practice.`}
                />
              </Form.Text>
            </Form.Group>
            <Form.Row>
              <Col sm={4} xs={12}>
                <Form.Group>
                  <Form.Label className={sFormLabel}>
                    <Translate tKey={`Preferred focus`} />
                  </Form.Label>
                  <Form.Control
                    type={"select"}
                    as={`select`}
                    className={sFormControl}
                    multiple={true}
                    name={`preferred-focus`}
                    id={`preferred-focus`}
                    value={formData.value["preferred-focus"]}
                  >
                    {optionsFocus(lang).map((item) => {
                      return (
                        <option
                          key={item.value}
                          onMouseDown={(e) => {
                            e.preventDefault();
                          }}
                          onClick={(e) => {
                            const originalValue =
                              formData.value["preferred-focus"] || [];
                            const wasChecked = (
                              formData.value["preferred-focus"] || []
                            ).includes(item.value);
                            let newValue: string[];
                            if (wasChecked) {
                              newValue = originalValue.filter(
                                (val) => val !== item.value
                              );
                            } else {
                              newValue = [...originalValue, item.value];
                            }
                            formData.change({
                              ...formData.value,
                              // @ts-ignore
                              "preferred-focus": newValue,
                            });
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          value={item.value}
                        >
                          {item.title}
                        </option>
                      );
                    })}
                  </Form.Control>
                  <Form.Control
                    className={sFormControl}
                    id={`preferred-focus-other`}
                    placeholder={translate(`Other option`, lang)}
                    name={`preferred-focus-other`}
                    onChange={function (param2) {
                      return chainDynamicValue(
                        formData,
                        "preferred-focus-other"
                      ).change(onChangeToValue(param2));
                    }}
                    value={
                      chainDynamicValue(formData, "preferred-focus-other").value
                    }
                  />
                  <Form.Text className={sNote}>
                    <Translate
                      tKey={`Select one or more options that your practice focuses on.`}
                    />
                  </Form.Text>
                  <Form.Text className={sNote}>
                    <Translate
                      tKey={`This information is not visible, but it does affect your profile, filtering, and search results.`}
                    />
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col sm={4} xs={12}>
                <Form.Group>
                  <Form.Label className={sFormLabel}>
                    <Translate tKey={`Preferred difficulty`} />
                  </Form.Label>
                  <Form.Control
                    type="select"
                    as={`select`}
                    className={sFormControl}
                    name={`preferred-difficulty`}
                    id={`preferred-difficulty`}
                    onChange={function (param2) {
                      return chainDynamicValue(
                        formData,
                        "preferred-difficulty"
                      ).change(onChangeToValue(param2));
                    }}
                    value={
                      chainDynamicValue(formData, "preferred-difficulty").value
                    }
                  >
                    {optionsDifficulty(lang).map((item) => {
                      return (
                        <option key={item.value} value={item.value}>
                          {item.title}
                        </option>
                      );
                    })}
                  </Form.Control>
                  <Form.Text className={sNote}>
                    <Translate
                      tKey={`If you are targeting a certain level of difficulty, select it.`}
                    />
                  </Form.Text>
                  <Form.Text className={sNote}>
                    <Translate
                      tKey={`This information is not visible, but it does affect your profile, filtering, and search results.`}
                    />
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col sm={4} xs={12}>
                <Form.Group>
                  <Form.Label className={sFormLabel}>
                    <Translate tKey={`Main language`} />
                  </Form.Label>
                  <FormSelect
                    as={`select`}
                    options={optionsLocale(`language`, lang)}
                    className={sFormControl}
                    name={`language-main`}
                    id={`language-main`}
                    onChange={function (param2) {
                      return chainDynamicValue(
                        formData,
                        "language-main"
                      ).change(onChangeToValue(param2));
                    }}
                    value={chainDynamicValue(formData, "language-main").value}
                  />
                  <Form.Text className={sNote}>
                    <Translate
                      tKey={`You can have a profile in multiple languages, select the main one.`}
                    />
                  </Form.Text>
                </Form.Group>
              </Col>
            </Form.Row>
            <Form.Row>
              <Col sm={8} xs={12}>
                <Form.Group>
                  <Form.Label className={sFormLabel}>
                    <Translate tKey={`Video medallion about you`} />
                  </Form.Label>
                  <Form.Control
                    className={sFormControl}
                    name={`video-medallion`}
                    id={`video-medallion`}
                    onChange={function (param2) {
                      return chainDynamicValue(
                        formData,
                        "video-medallion"
                      ).change(onChangeToValue(param2));
                    }}
                    value={chainDynamicValue(formData, "video-medallion").value}
                  />
                  <Form.Text className={sNote}>
                    <Translate
                      tKey={`Provide a link to the video (YouTube, Vimeo, etc.) in which you introduce yourself and what you offer. The video should be a maximum of 1 minute long.`}
                    />
                  </Form.Text>
                </Form.Group>
              </Col>
            </Form.Row>
            <Form.Group>
              <Form.Label className={sFormLabel}>
                <Translate tKey={`Short description about yourself in label`} />
              </Form.Label>
              <Form.Control
                as={`textarea`}
                rows={3}
                className={sFormControl}
                name={`short-description`}
                id={`short-description`}
                onChange={function (param2) {
                  return chainDynamicValue(
                    formData,
                    "short-description"
                  ).change(onChangeToValue(param2));
                }}
                value={chainDynamicValue(formData, "short-description").value}
              />
              <Form.Text className={sNote}>
                <Translate
                  tKey={`The short description will not be displayed directly in your profile, but will be displayed on other pages, eg in the class detail. Max. length is 512 characters.`}
                />
              </Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label className={sFormLabel}>
                <Translate
                  tKey={`Longer description about yourself in profile`}
                />
              </Form.Label>
              <Form.Control
                as={`textarea`}
                rows={10}
                className={sFormControl}
                name={`long-description`}
                id={`long-description`}
                onChange={function (param2) {
                  return chainDynamicValue(formData, "long-description").change(
                    onChangeToValue(param2)
                  );
                }}
                value={chainDynamicValue(formData, "long-description").value}
              />
              <Form.Text className={sNote}>
                <Translate
                  tKey={`The longer description will be listed directly in your profile. The first paragraph is the most important because it will be immediately visible. Max. length is 1.800 characters.`}
                />
              </Form.Text>
            </Form.Group>
            <Form.Row>
              <Col xs={12} sm={8}>
                <Form.Group>
                  <Form.Label className={sFormLabel}>
                    <Translate tKey={`Place on the map`} />
                  </Form.Label>
                  <Form.Control
                    className={sFormControl}
                    name={`place`}
                    id={`place`}
                    onChange={function (param2) {
                      return chainDynamicValue(formData, "place").change(
                        onChangeToValue(param2)
                      );
                    }}
                    value={chainDynamicValue(formData, "place").value}
                  />
                  <Form.Text className={sNote}>
                    <Translate
                      tKey={`Enter the address of your place of business as you would enter it into a search engine.`}
                    />
                  </Form.Text>
                </Form.Group>
              </Col>
            </Form.Row>
          </Card.Body>
        </AccordionCollapse>
      </Card>
    </Accordion>
  );
}

export default FormJoinUsYogaProfile;
